import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["previewMap", "editMap", "list", "readout", "error", "hint"]
  static values = { route: String }

  connect() {
    this.points = []
    this.geometry = null
    this.lengthM = null
    this.durationS = null
    this.recomputeTimer = null
    this.insertAt = null

    this.previewMap = null
    this.previewLayer = null

    this.editMap = null
    this.editMarkers = []
    this.editLayer = null

    this.routeInput = document.querySelector('#article_route')

    this.hydrateFromValue()
    this.buildPreviewMap()
    this.renderPreview()
    this.renderReadout()
    this.renderList()

    this.modalEl = this.element.querySelector('.modal')
    if (this.modalEl) {
      this.onModalShown = () => this.onShown()
      this.modalEl.addEventListener('shown.bs.modal', this.onModalShown)
    }
  }

  disconnect() {
    if (this.recomputeTimer) clearTimeout(this.recomputeTimer)
    if (this.modalEl) this.modalEl.removeEventListener('shown.bs.modal', this.onModalShown)
    if (this.previewMap) this.previewMap.remove()
    if (this.editMap) this.editMap.remove()
  }

  hydrateFromValue() {
    if (!this.routeValue) return
    let parsed
    try { parsed = JSON.parse(this.routeValue) } catch (e) { return }
    if (!parsed?.points?.length) return

    this.points = parsed.points.map(p => [p[0], p[1]])
    this.geometry = parsed.geometry || null
    this.lengthM = parsed.length_m ?? null
    this.durationS = parsed.duration_s ?? null
  }

  mainPointLatLng() {
    const latInput = document.querySelector('#article_latitude')
    const lonInput = document.querySelector('#article_longitude')
    if (!latInput || !lonInput) return null
    const latField = parseFloat(latInput.value)
    const lonField = parseFloat(lonInput.value)
    if (Number.isNaN(latField) || Number.isNaN(lonField)) return null
    return [lonField, latField]
  }

  buildPreviewMap() {
    if (!this.hasPreviewMapTarget) return
    this.previewMap = L.map(this.previewMapTarget, {
      zoomControl: false,
      attributionControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      tap: false,
      touchZoom: false,
    }).setView([50.1285663647049, 14.471028555510955], 7)
    L.tileLayer(`https://api.mapy.com/v1/maptiles/basic/256/{z}/{x}/{y}?apikey=${MAPY_API_KEY}`, {
      minZoom: 0,
      maxZoom: 17,
      attribution: '<a href="https://api.mapy.com/copyright" target="_blank">&copy; Seznam.cz a.s. a další</a>',
    }).addTo(this.previewMap)
  }

  renderPreview() {
    if (!this.previewMap) return
    if (this.previewLayer) {
      this.previewLayer.remove()
      this.previewLayer = null
    }
    if (this.geometry) {
      this.previewLayer = L.geoJSON(this.geometry, {
        style: { color: '#ff4e00', weight: 4, opacity: 0.9 }
      }).addTo(this.previewMap)
      this.previewMap.fitBounds(this.previewLayer.getBounds(), { padding: [10, 10] })
    } else {
      const ll = this.mainPointLatLng()
      if (ll) {
        this.previewMap.setView(ll, 9)
      } else {
        this.previewMap.setView([50.1285663647049, 14.471028555510955], 7)
      }
    }
  }

  onShown() {
    if (!this.editMap) {
      this.buildEditMap()
    } else {
      this.editMap.invalidateSize()
    }
    this.renderEditLayers()
    this.fitEditMap()
  }

  buildEditMap() {
    this.editMap = L.map(this.editMapTarget).setView([50.1285663647049, 14.471028555510955], 7)
    L.tileLayer(`https://api.mapy.com/v1/maptiles/basic/256/{z}/{x}/{y}?apikey=${MAPY_API_KEY}`, {
      minZoom: 0,
      maxZoom: 17,
      attribution: '<a href="https://api.mapy.com/copyright" target="_blank">&copy; Seznam.cz a.s. a další</a>',
    }).addTo(this.editMap)
    this.editMap.on('click', (e) => this.addPoint(e.latlng))
  }

  renderEditLayers() {
    this.editMarkers.forEach(m => m.remove())
    this.editMarkers = []
    if (this.editLayer) {
      this.editLayer.remove()
      this.editLayer = null
    }
    this.renderList()
    if (!this.editMap) return

    if (this.geometry) {
      this.editLayer = L.geoJSON(this.geometry, {
        style: { color: '#ff4e00', weight: 4, opacity: 0.9 }
      }).addTo(this.editMap)
    }
    this.points.forEach(([lon, lat], i) => {
      const m = L.marker([lat, lon], { icon: this.numberedIcon(i + 1, false) }).addTo(this.editMap)
      this.editMarkers.push(m)
    })
  }

  fitEditMap() {
    if (!this.editMap) return
    if (this.points.length >= 2) {
      const latLngs = this.points.map(([lon, lat]) => [lat, lon])
      this.editMap.fitBounds(latLngs, { padding: [30, 30] })
    } else if (this.points.length === 1) {
      const [lon, lat] = this.points[0]
      this.editMap.setView([lat, lon], 11)
    } else {
      const ll = this.mainPointLatLng()
      if (ll) {
        this.editMap.setView(ll, 10)
      } else {
        this.editMap.setView([50.1285663647049, 14.471028555510955], 7)
      }
    }
  }

  addPoint({ lat, lng }) {
    if (this.points.length >= 17) {
      this.errorTarget.textContent = "Maximum 17 bodů na trase."
      return
    }
    this.errorTarget.textContent = ""

    if (this.insertAt !== null) {
      this.points.splice(this.insertAt, 0, [lng, lat])
      this.insertAt = null
      this.updateHint()
      this.updateMapCursor()
    } else {
      this.points.push([lng, lat])
    }

    this.renderEditLayers()
    this.scheduleRecompute()
  }

  removePoint(event) {
    event.stopPropagation()
    const idx = parseInt(event.currentTarget.dataset.index, 10)
    if (Number.isNaN(idx)) return
    this.points.splice(idx, 1)
    this.insertAt = null
    this.updateHint()
    this.updateMapCursor()
    this.renderEditLayers()
    this.scheduleRecompute()
  }

  insertHere(event) {
    event.stopPropagation()
    const idx = parseInt(event.currentTarget.dataset.insertAt, 10)
    if (Number.isNaN(idx)) return
    this.insertAt = (this.insertAt === idx) ? null : idx
    this.renderList()
    this.updateHint()
    this.updateMapCursor()
  }

  updateHint() {
    if (!this.hasHintTarget) return
    this.hintTarget.textContent = (this.insertAt !== null)
      ? `Klikněte na mapu pro vložení bodu na pozici ${this.insertAt + 1}.`
      : 'Klikněte na mapu pro přidání bodu trasy. Maximum 17 bodů.'
  }

  updateMapCursor() {
    if (!this.editMap) return
    this.editMap.getContainer().style.cursor = (this.insertAt !== null) ? 'crosshair' : ''
  }

  clearAll() {
    if (!window.confirm("Opravdu vymazat všechny body trasy?")) return
    if (this.recomputeTimer) {
      clearTimeout(this.recomputeTimer)
      this.recomputeTimer = null
    }
    this.points = []
    this.geometry = null
    this.lengthM = null
    this.durationS = null
    this.insertAt = null
    this.errorTarget.textContent = ""
    this.renderEditLayers()
    this.renderPreview()
    this.renderReadout()
    this.updateHint()
    this.updateMapCursor()
    this.writeHiddenField()
    this.fitEditMap()
  }

  highlightPoint(event) {
    const idx = parseInt(event.currentTarget.dataset.index, 10)
    const marker = this.editMarkers[idx]
    if (!marker) return
    marker.setIcon(this.numberedIcon(idx + 1, true))
    marker.setZIndexOffset(1000)
  }

  unhighlightPoint(event) {
    const idx = parseInt(event.currentTarget.dataset.index, 10)
    const marker = this.editMarkers[idx]
    if (!marker) return
    marker.setIcon(this.numberedIcon(idx + 1, false))
    marker.setZIndexOffset(0)
  }

  scheduleRecompute() {
    if (this.recomputeTimer) clearTimeout(this.recomputeTimer)
    this.recomputeTimer = setTimeout(() => {
      this.recomputeTimer = null
      this.recompute()
    }, 200)
  }

  async recompute() {
    if (this.points.length < 2) {
      this.geometry = null
      this.lengthM = null
      this.durationS = null
      this.renderEditLayers()
      this.renderPreview()
      this.renderReadout()
      this.writeHiddenField()
      return
    }

    const start = `${this.points[0][0]},${this.points[0][1]}`
    const end = `${this.points[this.points.length - 1][0]},${this.points[this.points.length - 1][1]}`
    const middle = this.points.slice(1, -1).map(([lon, lat]) => `${lon},${lat}`).join(';')

    const params = new URLSearchParams({
      start, end,
      routeType: 'car_fast',
      format: 'geojson',
      apikey: MAPY_API_KEY,
    })
    if (middle) params.set('waypoints', middle)

    try {
      const res = await fetch(`https://api.mapy.com/v1/routing/route?${params.toString()}`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()

      this.geometry = data.geometry
      this.lengthM = data.length
      this.durationS = data.duration
      this.errorTarget.textContent = ""
      this.renderEditLayers()
      this.renderPreview()
      this.renderReadout()
      this.writeHiddenField()
    } catch (e) {
      this.errorTarget.textContent = `Nepodařilo se spočítat trasu (${e.message}).`
    }
  }

  renderList() {
    if (!this.hasListTarget) return
    if (!this.points.length) {
      this.listTarget.innerHTML = '<div class="text-muted small p-2">Klikněte na mapu pro přidání bodu.</div>'
      return
    }
    let html = ''
    this.points.forEach(([lon, lat], i) => {
      const activeClass = this.insertAt === i ? ' active' : ''
      html += `<button type="button" class="route-insert-zone${activeClass}"
                       data-action="click->form_route#insertHere"
                       data-insert-at="${i}"
                       aria-label="Vložit bod na pozici ${i + 1}"></button>`
      html += `<div class="route-point-row d-flex justify-content-between align-items-center"
                    data-index="${i}"
                    data-action="mouseenter->form_route#highlightPoint mouseleave->form_route#unhighlightPoint">
                 <span><span class="route-point-num">${i + 1}</span><span class="text-muted small">${lat.toFixed(5)}, ${lon.toFixed(5)}</span></span>
                 <button type="button" class="btn btn-sm btn-outline-danger"
                         data-action="click->form_route#removePoint"
                         data-index="${i}" aria-label="Odebrat bod">×</button>
               </div>`
    })
    this.listTarget.innerHTML = html
  }

  renderReadout() {
    let text = ''
    if (this.lengthM != null && this.durationS != null) {
      const km = (this.lengthM / 1000).toFixed(1)
      const min = Math.round(this.durationS / 60)
      text = `${km} km · ${min} min`
    }
    this.readoutTargets.forEach(el => el.textContent = text)
  }

  numberedIcon(num, active) {
    const bg = active ? '#ff4e00' : '#024e79'
    const size = active ? 32 : 24
    const font = active ? 14 : 12
    return L.divIcon({
      className: 'route-waypoint-marker',
      html: `<div style="background:${bg};color:#fff;border-radius:50%;width:${size}px;height:${size}px;line-height:${size}px;text-align:center;font-size:${font}px;font-weight:bold;border:2px solid #fff;box-shadow:0 0 5px rgba(0,0,0,0.5);">${num}</div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    })
  }

  writeHiddenField() {
    if (!this.routeInput) return
    if (this.points.length >= 2 && this.geometry) {
      this.routeInput.value = JSON.stringify({
        route_type: 'car_fast',
        points: this.points,
        geometry: this.geometry,
        length_m: this.lengthM,
        duration_s: this.durationS,
      })
    } else {
      this.routeInput.value = ''
    }
  }
}
